import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from 'src/store/store';
import guidesApiService from 'src/api/services/guidesApiService';
import { AirportListState } from './types';
import { notification } from 'antd';

const initialState: AirportListState = {
    isLoading: false,
    items: [],
    filter: {}
};

export const loadAirportsAsync = createAsyncThunk('airports/load', async () => {
    try {
        const data = await guidesApiService.getAirports();
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
            throw error;
        }
        throw new Error();
    }
});

const slice = createSlice({
    name: 'airports',
    initialState,
    reducers: {
        reset: () => initialState,
        nameFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.name = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadAirportsAsync.pending, (state) => {
                state.isLoading = true;
                state.items = [];
            })
            .addCase(loadAirportsAsync.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload;
            })
            .addCase(loadAirportsAsync.rejected, (state) => {
                state.isLoading = false;
            });
    }
});

export const { reset, nameFilterChanged } = slice.actions;

const selectAirports = (state: AppState) => state.airports;

const selectItems = (state: AppState) => selectAirports(state).items;

export const selectFilter = (state: AppState) => selectAirports(state).filter;

export const selectFilteredItems = createSelector([selectItems, selectFilter], (items, filter) => {
    let result = items;
    if (filter.name) {
        result = result.filter((item) => item.name.toLowerCase().includes(filter.name!.toLowerCase()));
    }
    return result;
});

export const selectTotalCount = createSelector(selectFilteredItems, (items) => items.length);

export const selectIsLoading = (state: AppState) => selectAirports(state).isLoading;

export default slice.reducer;
